import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { SiGmail } from "react-icons/si";
import './GmailInterface.css';
import { demoMessages, demoResponses, responseTemplates, welcomeEmail } from './content/GmailMessages';

interface Message {
  id: number;
  sender: 'user' | 'agent';
  text: string;
  timestamp: Date;
  isLoading?: boolean;
  toolCalls?: ToolCall[];
  subject?: string;
  from?: string;
  to?: string;
}

interface ToolCall {
  tool: string;
  input?: any;
  output?: any;
  status: 'running' | 'complete' | 'error';
}

interface GmailInterfaceProps {
  initialMessages?: Message[];
  onSendMessage: (message: string) => void;
  autoDemo?: boolean;
}

const GmailInterface: React.FC<GmailInterfaceProps> = ({ initialMessages = [], onSendMessage, autoDemo = true }) => {
  const [messages, setMessages] = useState<Message[]>(initialMessages.length ? initialMessages : [
    {
      id: 1,
      sender: 'agent',
      from: 'Michael Johnson <michael@salesteam.com>',
      to: 'me',
      subject: 'Welcome - Your Personal Sales Assistant',
      text: welcomeEmail.text,
      timestamp: new Date(Date.now() - 3600000),
    }
  ]);
  const [composing, setComposing] = useState(false);
  const [replyMode, setReplyMode] = useState(false);
  const [inputSubject, setInputSubject] = useState('');
  const [inputTo, setInputTo] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [demoComplete, setDemoComplete] = useState(false);
  const [demoInProgress, setDemoInProgress] = useState(false);
  const demoRunningRef = useRef(false);
  const demoLoopCountRef = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesRef = useRef<Message[]>(messages);

  // Keep the messagesRef updated with the latest messages
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Sample categories for Gmail sidebar
  const categories = [
    { name: 'Inbox', unread: 1, active: true },
    { name: 'Starred', unread: 0, active: false },
    { name: 'Snoozed', unread: 0, active: false },
    { name: 'Sent', unread: 0, active: false },
    { name: 'Drafts', unread: 2, active: false },
    { name: 'Important', unread: 0, active: false },
    { name: 'Spam', unread: 0, active: false },
    { name: 'Trash', unread: 0, active: false },
  ];

  const labels = [
    { name: 'Work', color: 'bg-yellow-500' },
    { name: 'Personal', color: 'bg-green-500' },
    { name: 'Finance', color: 'bg-purple-500' },
    { name: 'Social', color: 'bg-blue-500' },
  ];

  // Fisher-Yates shuffle algorithm for randomizing message order
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Auto demo functionality
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (autoDemo && demoMessages.length > 0 && !demoComplete && !demoInProgress && !demoRunningRef.current && (messages.length <= 1 || demoLoopCountRef.current > 0)) {
      setDemoInProgress(true);
      demoRunningRef.current = true;
      runDemoSequence();
    }
  }, [autoDemo, messages.length, demoComplete, demoInProgress]);
  
  // Disabled exhaustive-deps for this effect because runDemoSequence would create a circular reference

  // Scroll to bottom of messages when new ones are added
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      // Get the parent scrollable container
      const container = messagesEndRef.current.closest('.custom-scrollbar');
      if (container) {
        // Scroll the container instead of using scrollIntoView
        container.scrollTop = container.scrollHeight;
      }
    }
  };

  // Function to simulate typing and sending a message
  const simulateTyping = (message: string, subject: string = '') => {
    return new Promise<void>((resolve) => {
      // Set up reply mode
      setComposing(true);
      setReplyMode(true);
      
      // Clear any existing text
      setInputValue('');
      
      // Simulate typing character by character
      let currentCharIndex = 0;
      const typingInterval = setInterval(() => {
        if (currentCharIndex <= message.length) {
          setInputValue(message.substring(0, currentCharIndex));
          currentCharIndex++;
          
          // Adjust height as we type
          if (inputRef.current) {
            inputRef.current.style.height = 'auto';
            inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 200)}px`;
          }
        } else {
          clearInterval(typingInterval);
          
          // Small pause after typing before sending
          setTimeout(() => {
            // Call a specific version of send that uses the current inputValue
            handleSendDemoMessage(message, subject);
            resolve();
          }, 500);
        }
      }, 10 + Math.random() * 10); // Fast typing speed for demos
    });
  };

  // Helper function to safely convert URLs to hyperlinks while avoiding double-linking
  const convertUrlsToLinks = (text: string): string => {
    // Skip URLs that are already part of an HTML anchor tag
    return text.replace(
      /(?<!<a[^>]*?>)(?<!href=["'])(https?:\/\/[^\s<"']+)(?![^<]*?<\/a>)/g, 
      '<a href="$1" target="_blank" style="color:#0066cc; text-decoration:underline;">$1</a>'
    );
  };

  // Special version of handleSendMessage specifically for the demo
  const handleSendDemoMessage = (demoMessage: string, customSubject: string = '') => {
    if (!demoMessage.trim()) return;
    
    // Create a unique message ID to prevent duplicates
    const messageId = Date.now();
    
    // Create and add the user message
    const prevMessage = messages[messages.length - 1];
    const userMessage: Message = {
      id: messageId,
      sender: 'user',
      text: demoMessage,
      timestamp: new Date(),
      from: 'me',
      to: prevMessage.from || 'AgentBot <agent@agentspot.ai>',
      subject: customSubject || `Re: ${prevMessage.subject}`
    };
    
    // Only add this message if it doesn't already exist
    setMessages(prev => {
      // Check if this message already exists
      const exists = prev.some(m => m.text === demoMessage && m.sender === 'user');
      if (exists) return prev;
      return [...prev, userMessage];
    });
    
    setInputValue('');
    // Keep compose active in demo mode too
    setComposing(true);
    // Keep reply mode active for better UX
    // setReplyMode(false);
    
    // Simulate agent responding with the predetermined response for this demo message
    setTimeout(() => {
      setIsTyping(true);
    }, 300);
    
    // Simulate agent processing time
    const processingTime = 1000 + Math.random() * 1000;
    
    setTimeout(() => {
      setIsTyping(false);
      
      // Use predefined responses for the demo
      const demoResponse = demoResponses[demoMessage];
      if (!demoResponse) return;
      
      // Prepare agent response
      const responseSubject = demoResponse.subject || `Re: ${userMessage.subject}`;
      const initialResponseText = "I'm working on your request. Let me process that for you.";
      
      const responseId = Date.now() + 1;
      const botResponse: Message = {
        id: responseId,
        sender: 'agent',
        from: 'AgentBot <agent@agentspot.ai>',
        to: 'me',
        subject: responseSubject,
        text: initialResponseText,
        timestamp: new Date(),
        toolCalls: [
          {
            tool: demoResponse.tool,
            input: demoResponse.input,
            status: 'running',
          }
        ]
      };
      
      // Only add this message if the user message exists and this bot response doesn't
      setMessages(prev => {
        // Check if we already have this response 
        const userExists = prev.some(m => m.text === demoMessage && m.sender === 'user');
        const botExists = prev.some(m => 
          m.sender === 'agent' && 
          m.text === initialResponseText &&
          m.toolCalls?.some(tc => tc.tool === demoResponse.tool)
        );
        
        if (!userExists || botExists) return prev;
        return [...prev, botResponse];
      });
      
      scrollToBottom();
      
      // Tools always run and complete successfully
      const toolCompletionTime = 1500 + Math.random() * 1000;
      
      setTimeout(() => {
        setMessages(prev => {
          const updatedMessages = [...prev];
          // Find the exact message by comparing content rather than just ID
          const lastMessage = updatedMessages.find(m => 
            m.sender === 'agent' && 
            m.text === initialResponseText &&
            m.toolCalls?.some(tc => tc.tool === demoResponse.tool)
          );
          
          if (lastMessage?.toolCalls && lastMessage.toolCalls.length > 0) {
            const toolCall = lastMessage.toolCalls[0];
            toolCall.status = 'complete';
            toolCall.output = demoResponse.output;
            
            // Update the response text using the imported responseTemplates
            const templateText = responseTemplates[toolCall.tool] || "I've completed your request. Here are the results.";
            
            // Process the template to replace placeholders with actual values
            let formattedText = templateText;
            
            // Replace available_slots with table rows if they exist
            if (formattedText.includes('{available_slots}') && toolCall.output.available_slots) {
              const slotsRows = toolCall.output.available_slots.map((slot: any) => {
                return `<tr>
                  <td style="padding:4px 8px; border:1px solid #d0d5dd;">${slot.date}</td>
                  <td style="padding:4px 8px; border:1px solid #d0d5dd;">${slot.time}</td>
                  <td style="padding:4px 8px; border:1px solid #d0d5dd;">${slot.duration} mins</td>
                </tr>`;
              }).join('');
              formattedText = formattedText.replace('{available_slots}', slotsRows);
            }
            
            // Replace materials with table rows if they exist
            if (formattedText.includes('{materials}') && toolCall.output.materials) {
              const materialsRows = toolCall.output.materials.map((material: any, idx: number) => {
                const emojis = ['📄', '📊', '📑', '📋'];
                return `<tr>
                  <td style="padding:4px 8px; border:1px solid #d0d5dd;">${emojis[idx % emojis.length]} ${material.name}</td>
                  <td style="padding:4px 8px; border:1px solid #d0d5dd;">${material.type.replace('_', ' ')}</td>
                  <td style="padding:4px 8px; border:1px solid #d0d5dd;">${material.size}</td>
                </tr>`;
              }).join('');
              formattedText = formattedText.replace('{materials}', materialsRows);
            }
            
            // Replace plans with table rows if they exist
            if (formattedText.includes('{plans}') && toolCall.output.plans) {
              const plansRows = toolCall.output.plans.map((plan: any) => {
                // Handle different data structures that might be present in mock data
                const monthly = plan.monthly || plan.price || '-';
                const annual = plan.annual || '-';
                const savings = plan.savings || '-';
                
                return `<tr>
                  <td style="padding:4px 8px; border:1px solid #d0d5dd;"><strong>${plan.name}</strong></td>
                  <td style="padding:4px 8px; border:1px solid #d0d5dd;">${monthly}</td>
                  <td style="padding:4px 8px; border:1px solid #d0d5dd;">${annual}</td>
                  <td style="padding:4px 8px; border:1px solid #d0d5dd;"><span style="color:#4CAF50;">${savings}</span></td>
                </tr>`;
              }).join('');
              formattedText = formattedText.replace('{plans}', plansRows);
            }
            
            // Replace sections if they exist
            if (formattedText.includes('{sections}') && toolCall.output.sections) {
              const sectionsText = toolCall.output.sections.map((section: string, idx: number) => {
                const emojis = ['📊', '💹', '🌍', '🔝', '👥', '📋', '📝'];
                return `• ${emojis[idx % emojis.length]} ${section}`;
              }).join('\n');
              formattedText = formattedText.replace('{sections}', sectionsText);
            }
            
            // Replace subject if it exists
            if (formattedText.includes('{subject}') && toolCall.output.subject) {
              formattedText = formattedText.replace('{subject}', toolCall.output.subject);
            }
            
            // Replace word_count if it exists
            if (formattedText.includes('{word_count}') && toolCall.output.word_count) {
              formattedText = formattedText.replace('{word_count}', toolCall.output.word_count);
            }
            
            // Replace reservation_id if it exists
            if (formattedText.includes('{reservation_id}') && toolCall.output.reservation_id) {
              formattedText = formattedText.replace('{reservation_id}', toolCall.output.reservation_id);
            }
            
            // Replace airline if it exists
            if (formattedText.includes('{airline}') && toolCall.output.airline) {
              formattedText = formattedText.replace('{airline}', toolCall.output.airline);
            }
            
            // Replace flight_number if it exists
            if (formattedText.includes('{flight_number}') && toolCall.output.flight_number) {
              formattedText = formattedText.replace('{flight_number}', toolCall.output.flight_number);
            }
            
            // Replace departure details if they exist
            if (toolCall.output.departure) {
              if (formattedText.includes('{departure.airport}')) {
                formattedText = formattedText.replace('{departure.airport}', toolCall.output.departure.airport);
              }
              if (formattedText.includes('{departure.date}')) {
                formattedText = formattedText.replace('{departure.date}', toolCall.output.departure.date);
              }
              if (formattedText.includes('{departure.time}')) {
                formattedText = formattedText.replace('{departure.time}', toolCall.output.departure.time);
              }
            }
            
            // Replace arrival details if they exist
            if (toolCall.output.arrival) {
              if (formattedText.includes('{arrival.airport}')) {
                formattedText = formattedText.replace('{arrival.airport}', toolCall.output.arrival.airport);
              }
              if (formattedText.includes('{arrival.time}')) {
                formattedText = formattedText.replace('{arrival.time}', toolCall.output.arrival.time);
              }
            }
            
            // Replace meeting details if they exist
            if (formattedText.includes('{title}') && toolCall.output.title) {
              formattedText = formattedText.replace('{title}', toolCall.output.title);
            }
            if (formattedText.includes('{date}') && toolCall.output.date) {
              formattedText = formattedText.replace('{date}', toolCall.output.date);
            }
            if (formattedText.includes('{time}') && toolCall.output.time) {
              formattedText = formattedText.replace('{time}', toolCall.output.time);
            }
            if (formattedText.includes('{location}') && toolCall.output.location) {
              formattedText = formattedText.replace('{location}', toolCall.output.location);
            }
            if (formattedText.includes('{meeting_link}') && toolCall.output.meeting_link) {
              const url = toolCall.output.meeting_link;
              formattedText = formattedText.replace(/\{meeting_link\}/g, `<a href="${url}" target="_blank" style="color:#0066cc; text-decoration:underline;">${url}</a>`);
            }
            if (formattedText.includes('{attendees.length}') && toolCall.output.attendees) {
              formattedText = formattedText.replace('{attendees.length}', toolCall.output.attendees.length);
            }
            
            // For update_meeting tool
            if (formattedText.includes('{old_schedule_date}') && toolCall.output.old_schedule) {
              formattedText = formattedText.replace('{old_schedule_date}', toolCall.output.old_schedule.date);
            }
            if (formattedText.includes('{old_schedule_time}') && toolCall.output.old_schedule) {
              formattedText = formattedText.replace('{old_schedule_time}', toolCall.output.old_schedule.time);
            }
            if (formattedText.includes('{new_schedule_date}') && toolCall.output.new_schedule) {
              formattedText = formattedText.replace('{new_schedule_date}', toolCall.output.new_schedule.date);
            }
            if (formattedText.includes('{new_schedule_time}') && toolCall.output.new_schedule) {
              formattedText = formattedText.replace('{new_schedule_time}', toolCall.output.new_schedule.time);
            }
            if (formattedText.includes('{attendees_notified_length}') && toolCall.output.attendees_notified) {
              formattedText = formattedText.replace('{attendees_notified_length}', toolCall.output.attendees_notified.length);
            }
            
            // Old format for backward compatibility
            if (formattedText.includes('{old_schedule.date}') && toolCall.output.old_schedule) {
              formattedText = formattedText.replace('{old_schedule.date}', toolCall.output.old_schedule.date);
            }
            if (formattedText.includes('{old_schedule.time}') && toolCall.output.old_schedule) {
              formattedText = formattedText.replace('{old_schedule.time}', toolCall.output.old_schedule.time);
            }
            if (formattedText.includes('{new_schedule.date}') && toolCall.output.new_schedule) {
              formattedText = formattedText.replace('{new_schedule.date}', toolCall.output.new_schedule.date);
            }
            if (formattedText.includes('{new_schedule.time}') && toolCall.output.new_schedule) {
              formattedText = formattedText.replace('{new_schedule.time}', toolCall.output.new_schedule.time);
            }
            if (formattedText.includes('{attendees_notified.length}') && toolCall.output.attendees_notified) {
              formattedText = formattedText.replace('{attendees_notified.length}', toolCall.output.attendees_notified.length);
            }
            
            // Replace highlights if they exist
            if (formattedText.includes('{highlights}') && toolCall.output.highlights) {
              const highlightsText = toolCall.output.highlights.map((highlight: string, idx: number) => {
                const emojis = ['🔝', '🚀', '🌍', '📉', '💰'];
                return `• ${emojis[idx % emojis.length]} ${highlight}`;
              }).join('\n');
              formattedText = formattedText.replace('{highlights}', highlightsText);
            }
            
            // Replace key_metrics if they exist
            if (formattedText.includes('{key_metrics}') && toolCall.output.key_metrics) {
              const metricsText = toolCall.output.key_metrics.map((metric: any, idx: number) => {
                const emojis = ['💰', '📈', '💹', '💼', '🏦'];
                return `• ${emojis[idx % emojis.length]} ${metric.name || 'Metric'}: ${metric.value || 'N/A'} (${metric.change || 'N/A'})`;
              }).join('\n');
              formattedText = formattedText.replace('{key_metrics}', metricsText);
            }
            
            // Replace sentiment emoji if it exists
            if (formattedText.includes('{sentiment_emoji}')) {
              const sentimentEmoji = toolCall.output.sentiment === 'positive' ? '😀 positive' : 
                                     toolCall.output.sentiment === 'negative' ? '😟 negative' : 
                                     '😐 neutral';
              formattedText = formattedText.replace('{sentiment_emoji}', sentimentEmoji);
            }
            
            // Format additional_benefits with more spacing for readability
            if (formattedText.includes('{additional_benefits}') && toolCall.output.additional_benefits) {
              const benefitsText = toolCall.output.additional_benefits.map((benefit: string, idx: number) => {
                const emojis = ['✨', '🎁', '🔑'];
                return `<div style="margin-bottom:4px;">• ${emojis[idx % emojis.length]} ${benefit}</div>`;
              }).join('');
              formattedText = formattedText.replace('{additional_benefits}', benefitsText);
            }
            
            // Replace booking_link if it exists
            if (formattedText.includes('{booking_link}') && toolCall.output.booking_link) {
              const url = toolCall.output.booking_link;
              formattedText = formattedText.replace(/\{booking_link\}/g, `<a href="${url}" target="_blank" style="color:#0066cc; text-decoration:underline;">${url}</a>`);
            }
            
            // Replace comparison_url if it exists
            if (formattedText.includes('{comparison_url}') && toolCall.output.comparison_url) {
              const url = toolCall.output.comparison_url;
              formattedText = formattedText.replace(/\{comparison_url\}/g, `<a href="${url}" target="_blank" style="color:#0066cc; text-decoration:underline;">${url}</a>`);
            }
            
            // Convert any remaining plain URLs to clickable links, but avoid double-linking
            formattedText = convertUrlsToLinks(formattedText);
            
            // Update the message text
            lastMessage.text = formattedText;
          }
          
          return updatedMessages.map(m => 
            m.sender === 'agent' && 
            m.text === initialResponseText && 
            m.toolCalls?.some(tc => tc.tool === demoResponse.tool) && 
            lastMessage ? lastMessage : m
          );
        });
        
        scrollToBottom();
      }, toolCompletionTime);
    }, processingTime);
  };

  // Run the full demo sequence
  const runDemoSequence = async () => {
    // Increment loop counter
    demoLoopCountRef.current++;
    console.log(`Starting Gmail demo loop ${demoLoopCountRef.current}/1`);
    
    // Wait for initial render
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Randomize the order of messages for this loop
    const randomizedMessages = shuffleArray(demoMessages);
    
    // Loop through each demo message
    for (let i = 0; i < randomizedMessages.length; i++) {
      // Wait for previous message to be processed
      if (i > 0) {
        // Wait for agent to finish responding to previous message
        await new Promise(resolve => {
          const checkInterval = setInterval(() => {
            // Use messagesRef to get the latest messages
            const currentMessages = messagesRef.current;
            const lastMessage = currentMessages[currentMessages.length - 1];
            
            if (lastMessage && lastMessage.sender === 'agent' && 
                (!lastMessage.toolCalls || 
                 lastMessage.toolCalls.every(tc => tc.status === 'complete'))) {
              clearInterval(checkInterval);
              // Add some delay between conversations
              setTimeout(resolve, 2000);
            }
          }, 500);
        });
      }
      
      // Get the appropriate subject for this message if it exists in the demo responses
      const demoResponseData = demoResponses[randomizedMessages[i]];
      const subject = demoResponseData?.subject || '';
      
      // Prevent any default scroll behavior
      const preventScroll = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
      };
      
      // Add event listeners to prevent page scrolling during demo
      document.addEventListener('scroll', preventScroll, { passive: false });
      
      // Type and send the next message
      await simulateTyping(randomizedMessages[i], subject);
      
      // Remove the event listeners
      document.removeEventListener('scroll', preventScroll);
    }
    
    // Only run a single loop, then mark as complete
    demoRunningRef.current = false;
    setDemoInProgress(false);
    setDemoComplete(true);
    
    // Switch to the next platform (Telegram)
    const switchPlatformEvent = new CustomEvent('switchPlatform', {
      detail: { platform: 'telegram' }
    });
    window.dispatchEvent(switchPlatformEvent);
  };

  const handleSendMessage = useCallback(() => {
    if (inputValue.trim() === '') return;

    // Create message object based on compose or reply mode
    const newMessage: Message = {
      id: Date.now(),
      sender: 'user',
      text: inputValue,
      timestamp: new Date(),
      from: 'me',
      to: replyMode ? messages[messages.length - 1].from || 'AgentBot <agent@agentspot.ai>' : inputTo,
      subject: replyMode ? `Re: ${messages[messages.length - 1].subject}` : inputSubject
    };

    setMessages(prev => [...prev, newMessage]);
    onSendMessage(inputValue);
    setInputValue('');
    // Keep compose active
    setComposing(true);

    // Simulate agent typing
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      
      // Keep compose active
      setComposing(true);
      
      // Random selection of email tools
      const emailTools = ['calendar', 'check_availability', 'send_materials', 'update_meeting', 'pricing_info'];
      const selectedTool = emailTools[Math.floor(Math.random() * emailTools.length)];
      
      // Generate appropriate mock data based on tool
      let mockInput = {};
      let mockOutput = {};
      let responseSubject = `Re: ${newMessage.subject}`;
      
      switch (selectedTool) {
        case 'calendar':
          mockInput = { 
            action: "create_event",
            attendees: ["customer"],
            date: "next week",
            time: "11:00 AM", 
            duration: 45
          };
          mockOutput = { 
            event_id: `evt_${Math.floor(Math.random() * 100000)}`,
            title: "Product Demo Session",
            date: "2024-06-14",
            time: "11:00 AM - 11:45 AM",
            attendees: [
              "customer@example.com",
              "michael@salesteam.com",
              "demo@salesteam.com"
            ],
            location: "Zoom Video Conference",
            meeting_link: "https://zoom.us/j/987654321",
            status: "confirmed"
          };
          responseSubject = "Product Demo Scheduled";
          break;
          
        case 'check_availability':
          mockInput = { 
            sales_rep: "michael_johnson",
            week: "next",
            meeting_type: "consultation"
          };
          mockOutput = { 
            available_slots: [
              { date: "2024-06-12", time: "9:00 AM", duration: 30 },
              { date: "2024-06-12", time: "2:30 PM", duration: 30 },
              { date: "2024-06-14", time: "10:30 AM", duration: 30 },
              { date: "2024-06-14", time: "4:00 PM", duration: 30 }
            ],
            sales_rep: "Michael Johnson",
            timezone: "Eastern Time (ET)",
            booking_link: "https://calendly.com/michael-sales/consultation"
          };
          responseSubject = "Available Times for Consultation";
          break;
          
        case 'send_materials':
          mockInput = { 
            material_type: "product_info",
            plan: "professional",
            format: "pdf"
          };
          mockOutput = { 
            materials: [
              { name: "Professional Plan Overview", type: "brochure", size: "1.8 MB" },
              { name: "Feature Comparison", type: "comparison", size: "1.2 MB" },
              { name: "Customer Testimonials", type: "testimonials", size: "2.1 MB" }
            ],
            follow_up: {
              type: "call",
              recommended_date: "within 5 business days"
            },
            delivery_method: "email_attachment",
            status: "sent"
          };
          responseSubject = "Professional Plan Information";
          break;
          
        case 'update_meeting':
          mockInput = { 
            current_date: "Monday, June 10, 2024",
            new_date: "Wednesday, June 12, 2024",
            keep_time: true,
            meeting_id: "meet_" + Math.floor(Math.random() * 10000)
          };
          mockOutput = { 
            meeting_id: (mockInput as any).meeting_id,
            old_schedule: {
              date: "Monday, June 10, 2024",
              time: "10:00 AM - 11:00 AM",
            },
            new_schedule: {
              date: "Wednesday, June 12, 2024", 
              time: "10:00 AM - 11:00 AM",
            },
            attendees_notified: [
              "customer@example.com",
              "michael@salesteam.com"
            ],
            calendar_updated: true,
            status: "rescheduled"
          };
          responseSubject = "Meeting Rescheduled";
          break;
          
        case 'pricing_info':
        default:
          mockInput = { 
            pricing_type: "subscription",
            plan_period: "annual",
            discount_inquiry: true
          };
          mockOutput = { 
            discount_available: true,
            discount_percentage: "20%",
            plans: [
              { name: "Basic", monthly: "$49", annual: "$470", savings: "$118" },
              { name: "Professional", monthly: "$99", annual: "$950", savings: "$238" },
              { name: "Enterprise", monthly: "$299", annual: "$2,870", savings: "$718" }
            ],
            promotion_code: "ANNUAL20",
            expires: "2024-07-31",
            additional_benefits: [
              "Priority customer support",
              "Free onboarding session",
              "Quarterly business review"
            ]
          };
          responseSubject = "Annual Subscription Discount Information";
          break;
      }
      
      // This would normally come from your API
      const botResponse: Message = {
        id: Date.now() + 1,
        sender: 'agent',
        from: 'AgentBot <agent@agentspot.ai>',
        to: 'me',
        subject: responseSubject,
        text: "I'm working on your request. Let me process that for you.",
        timestamp: new Date(),
        toolCalls: [
          {
            tool: selectedTool,
            input: mockInput,
            status: 'running'
          }
        ]
      };
      
      setMessages(prev => [...prev, botResponse]);
      
      // Simulate tool call completing
      setTimeout(() => {
        setMessages(prev => {
          const updated = [...prev];
          const lastMessage = updated[updated.length - 1];
          
          if (lastMessage.toolCalls && lastMessage.toolCalls.length > 0) {
            const toolCall = lastMessage.toolCalls[0];
            toolCall.status = 'complete';
            toolCall.output = mockOutput;
            
            // Format the template with actual values
            let toolTemplate = responseTemplates[toolCall.tool] || "I've completed your request. Here are the results.";
            
            // Replace placeholders for various data types in the template
            
            // Generate table rows for available slots
            if (toolCall.tool === 'check_availability' && toolCall.output.available_slots) {
              const slotsRows = toolCall.output.available_slots.map((slot: any) => {
                return `<tr>
                  <td style="padding:4px 8px; border:1px solid #d0d5dd;">${slot.date}</td>
                  <td style="padding:4px 8px; border:1px solid #d0d5dd;">${slot.time}</td>
                  <td style="padding:4px 8px; border:1px solid #d0d5dd;">${slot.duration} mins</td>
                </tr>`;
              }).join('');
              toolTemplate = toolTemplate.replace('{available_slots}', slotsRows);
            }
            
            // Generate table rows for materials
            if (toolCall.tool === 'send_materials' && toolCall.output.materials) {
              const materialsRows = toolCall.output.materials.map((material: any, idx: number) => {
                const emojis = ['📄', '📊', '📑', '📋'];
                return `<tr>
                  <td style="padding:4px 8px; border:1px solid #d0d5dd;">${emojis[idx % emojis.length]} ${material.name}</td>
                  <td style="padding:4px 8px; border:1px solid #d0d5dd;">${material.type}</td>
                  <td style="padding:4px 8px; border:1px solid #d0d5dd;">${material.size}</td>
                </tr>`;
              }).join('');
              toolTemplate = toolTemplate.replace('{materials}', materialsRows);
            }
            
            // Generate table rows for pricing plans
            if (toolCall.tool === 'pricing_info' && toolCall.output.plans) {
              const plansRows = toolCall.output.plans.map((plan: any) => {
                // Handle different data structures that might be present in mock data
                const monthly = plan.monthly || plan.price || '-';
                const annual = plan.annual || '-';
                const savings = plan.savings || '-';
                
                return `<tr>
                  <td style="padding:4px 8px; border:1px solid #d0d5dd;"><strong>${plan.name}</strong></td>
                  <td style="padding:4px 8px; border:1px solid #d0d5dd;">${monthly}</td>
                  <td style="padding:4px 8px; border:1px solid #d0d5dd;">${annual}</td>
                  <td style="padding:4px 8px; border:1px solid #d0d5dd;"><span style="color:#4CAF50;">${savings}</span></td>
                </tr>`;
              }).join('');
              toolTemplate = toolTemplate.replace('{plans}', plansRows);
            }
            
            // For calendar tool
            if (toolCall.tool === 'calendar') {
              if (toolCall.output.title) {
                toolTemplate = toolTemplate.replace('{title}', toolCall.output.title);
              }
              if (toolCall.output.date) {
                toolTemplate = toolTemplate.replace('{date}', toolCall.output.date);
              }
              if (toolCall.output.time) {
                toolTemplate = toolTemplate.replace('{time}', toolCall.output.time);
              }
              if (toolCall.output.location) {
                toolTemplate = toolTemplate.replace('{location}', toolCall.output.location);
              }
              if (toolCall.output.meeting_link) {
                const url = toolCall.output.meeting_link;
                toolTemplate = toolTemplate.replace(/\{meeting_link\}/g, `<a href="${url}" target="_blank" style="color:#0066cc; text-decoration:underline;">${url}</a>`);
              }
              if (toolCall.output.attendees) {
                toolTemplate = toolTemplate.replace('{attendees.length}', toolCall.output.attendees.length);
              }
            }
            
            // For check_availability tool
            if (toolCall.tool === 'check_availability') {
              if (toolCall.output.sales_rep) {
                toolTemplate = toolTemplate.replace('{sales_rep}', toolCall.output.sales_rep);
              }
              if (toolCall.output.timezone) {
                toolTemplate = toolTemplate.replace('{timezone}', toolCall.output.timezone);
              }
              if (toolCall.output.booking_link) {
                const url = toolCall.output.booking_link;
                toolTemplate = toolTemplate.replace(/\{booking_link\}/g, `<a href="${url}" target="_blank" style="color:#0066cc; text-decoration:underline;">${url}</a>`);
              }
            }
            
            // For update_meeting tool
            if (toolCall.tool === 'update_meeting') {
              if (toolCall.output.old_schedule) {
                toolTemplate = toolTemplate.replace('{old_schedule_date}', toolCall.output.old_schedule.date);
                toolTemplate = toolTemplate.replace('{old_schedule_time}', toolCall.output.old_schedule.time);
                // For backward compatibility
                toolTemplate = toolTemplate.replace('{old_schedule.date}', toolCall.output.old_schedule.date);
                toolTemplate = toolTemplate.replace('{old_schedule.time}', toolCall.output.old_schedule.time);
              }
              if (toolCall.output.new_schedule) {
                toolTemplate = toolTemplate.replace('{new_schedule_date}', toolCall.output.new_schedule.date);
                toolTemplate = toolTemplate.replace('{new_schedule_time}', toolCall.output.new_schedule.time);
                // For backward compatibility
                toolTemplate = toolTemplate.replace('{new_schedule.date}', toolCall.output.new_schedule.date);
                toolTemplate = toolTemplate.replace('{new_schedule.time}', toolCall.output.new_schedule.time);
              }
              if (toolCall.output.attendees_notified) {
                toolTemplate = toolTemplate.replace('{attendees_notified_length}', toolCall.output.attendees_notified.length);
                // For backward compatibility
                toolTemplate = toolTemplate.replace('{attendees_notified.length}', toolCall.output.attendees_notified.length);
              }
            }
            
            // For pricing_info tool
            if (toolCall.tool === 'pricing_info') {
              if (toolCall.output.discount_percentage) {
                toolTemplate = toolTemplate.replace('{discount_percentage}', toolCall.output.discount_percentage);
              }
              if (toolCall.output.promotion_code) {
                toolTemplate = toolTemplate.replace('{promotion_code}', toolCall.output.promotion_code);
              }
              if (toolCall.output.expires) {
                toolTemplate = toolTemplate.replace('{expires}', toolCall.output.expires);
              }
              if (toolCall.output.additional_benefits) {
                const benefitsText = toolCall.output.additional_benefits.map((benefit: string, idx: number) => {
                  const emojis = ['✨', '🎁', '🔑'];
                  return `<div style="margin-bottom:4px;">• ${emojis[idx % emojis.length]} ${benefit}</div>`;
                }).join('');
                toolTemplate = toolTemplate.replace('{additional_benefits}', benefitsText);
              }
              if (toolCall.output.comparison_url) {
                const url = toolCall.output.comparison_url;
                toolTemplate = toolTemplate.replace(/\{comparison_url\}/g, `<a href="${url}" target="_blank" style="color:#0066cc; text-decoration:underline;">${url}</a>`);
              }
            }
            
            // Convert any remaining plain URLs to clickable links, but avoid double-linking
            toolTemplate = convertUrlsToLinks(toolTemplate);
            
            // Update the message text with the processed template
            lastMessage.text = toolTemplate;
          }
          
          return updated;
        });
        
        // Keep compose active after agent response is complete
        setComposing(true);
        scrollToBottom();
      }, 3000);
    }, 2000);
  }, [inputValue, inputSubject, inputTo, messages, onSendMessage, replyMode]);

  const handleCompose = () => {
    setComposing(true);
    setReplyMode(false);
    setInputSubject('');
    setInputTo(''); 
    setInputValue('');
  };

  const handleReply = () => {
    setComposing(true);
    setReplyMode(true);
    setInputValue('');
  };

  const handleCancel = () => {
    setComposing(false);
    setReplyMode(false);
    setInputValue('');
  };

  // Handle textarea height adjustment
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    
    // Auto-resize textarea with smaller max height
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 100)}px`;
  };

  const formatTimestamp = (date: Date) => {
    const today = new Date();
    const isToday = date.getDate() === today.getDate() &&
                    date.getMonth() === today.getMonth() &&
                    date.getFullYear() === today.getFullYear();

    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className="flex h-[680px] max-w-5xl mx-auto rounded-lg overflow-hidden border border-gray-200 shadow-lg gmail-interface-root" style={{ contain: 'strict' }}>
      {/* Gmail sidebar */}
      <div className="w-48 bg-white border-r border-gray-200 flex flex-col">
        {/* Gmail logo */}
        <div className="p-2 flex items-center">
          {SiGmail({ className: "w-6 h-5", color: "#EA4335" })}
          <span className="ml-2 text-gray-600 font-medium text-sm">Gmail</span>
        </div>
        
        {/* Compose button */}
        <div className="p-2">
          <button 
            onClick={handleCompose}
            className="flex items-center px-4 py-2 rounded-lg bg-blue-50 hover:shadow-sm transition-shadow duration-150 text-gray-800 text-xs">
            <svg className="w-4 h-4 mr-2 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
            </svg>
            <span className="font-medium">Compose</span>
          </button>
        </div>
        
        {/* Categories */}
        <div className="overflow-y-auto flex-grow">
          <div className="px-1 py-2">
            <ul className="space-y-0.5">
              {categories.map((category) => (
                <li key={category.name}>
                  <button 
                    className={`flex items-center w-full px-2 py-1 rounded-r-full text-xs ${category.active ? 'bg-red-100 text-red-800 font-medium' : 'text-gray-700 hover:bg-gray-100'}`}
                  >
                    <span className="w-4">
                      {category.name === 'Inbox' ? (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19 3H4.99C3.89 3 3 3.89 3 4.99v14.02C3 20.11 3.89 21 4.99 21h14.02C20.11 21 21 20.11 21 19.01V4.99C21 3.89 20.11 3 19 3zm0 16.01H4.99V7L12 14l7-7v12.01z" />
                        </svg>
                      ) : category.name === 'Starred' ? (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                      ) : category.name === 'Sent' ? (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z" />
                        </svg>
                      )}
                    </span>
                    <span className="ml-2 flex-grow text-left">{category.name}</span>
                    {category.unread > 0 && (
                      <span className="text-2xs font-medium">{category.unread}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
            
            {/* Labels section */}
            <div className="mt-4 mb-1 px-2">
              <h3 className="text-2xs font-medium text-gray-500 uppercase">Labels</h3>
            </div>
            <ul className="space-y-0.5">
              {labels.map((label) => (
                <li key={label.name}>
                  <button className="flex items-center w-full px-2 py-1 rounded-r-full text-gray-700 hover:bg-gray-100 text-xs">
                    <span className={`w-2 h-2 rounded-full ${label.color}`}></span>
                    <span className="ml-3">{label.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      
      {/* Main email area */}
      <div className="flex-1 flex flex-col bg-white">
        {/* Toolbar */}
        <div className="border-b border-gray-200 p-1 flex items-center bg-gray-50">
          <div className="flex space-x-1">
            <button className="p-1.5 text-gray-500 hover:bg-gray-200 rounded">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
              </svg>
            </button>
            <button className="p-1.5 text-gray-500 hover:bg-gray-200 rounded">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z" />
              </svg>
            </button>
            <button className="p-1.5 text-gray-500 hover:bg-gray-200 rounded">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
              </svg>
            </button>
          </div>
          <div className="ml-auto flex space-x-1">
            <button className="p-1.5 text-gray-500 hover:bg-gray-200 rounded">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Email thread - with fixed height and smooth scrolling */}
        <div className="flex-1 p-2 bg-white custom-scrollbar" style={{ height: "calc(100% - 40px)", overflowY: "auto", scrollBehavior: "smooth", overscrollBehavior: "contain" }}>
          {messages.map((message, index) => (
            <motion.div 
              key={message.id} 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="mb-3 border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all fade-in">
              <div className="p-2 bg-gray-50 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-sm font-medium text-gray-800">{message.subject}</h2>
                  <span className="text-xs text-gray-500">{formatTimestamp(message.timestamp)}</span>
                </div>
                <div className="mt-1 flex items-center">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium text-xs">
                    {message.sender === 'agent' ? 'A' : 'M'}
                  </div>
                  <div className="ml-2">
                    <p className="text-xs font-medium text-gray-800">
                      {message.from}
                    </p>
                    <p className="text-2xs text-gray-500">
                      to {message.to}
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-3">
                <div className="text-xs whitespace-pre-wrap text-gray-800" dangerouslySetInnerHTML={{ __html: message.text }}></div>
              </div>
              {index === messages.length - 1 && (
                <div className="p-2 bg-gray-50 border-t border-gray-200 flex justify-start">
                  <button 
                    onClick={handleReply} 
                    className="px-3 py-0.5 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs font-medium"
                  >
                    Reply
                  </button>
                </div>
              )}
            </motion.div>
          ))}
          
          {/* Typing indicator */}
          {isTyping && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="mb-3 border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm"
            >
              <div className="p-2 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center">
                  <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium text-xs">
                    A
                  </div>
                  <div className="ml-2">
                    <p className="text-xs text-gray-500">
                      AgentBot is composing a reply... 
                      <span className="inline-flex ml-1">
                        <span className="animate-bounce mx-px">✍️</span>
                        <span className="animate-bounce mx-px delay-75">.</span>
                        <span className="animate-bounce mx-px delay-150">.</span>
                        <span className="animate-bounce mx-px delay-300">.</span>
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        {/* Compose email area */}
        {composing && (
          <div className="border-t border-gray-300 bg-white p-2">
            <div className="mb-2 border border-gray-300 rounded-lg overflow-hidden">
              <div className="bg-gray-100 px-3 py-1.5 flex justify-between items-center">
                <h3 className="text-gray-800 font-medium text-xs">
                  {replyMode ? `Reply to: ${messages[messages.length - 1].subject}` : 'New Message'}
                </h3>
                <button onClick={handleCancel} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </button>
              </div>
              
              {!replyMode && (
                <div className="p-2 border-b border-gray-300 space-y-2">
                  <div className="flex items-center">
                    <label className="w-12 text-xs text-gray-500">To:</label>
                    <input 
                      type="text" 
                      className="flex-1 outline-none text-xs" 
                      value={inputTo}
                      onChange={(e) => setInputTo(e.target.value)}
                      placeholder="recipient@example.com"
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="w-12 text-xs text-gray-500">Subject:</label>
                    <input 
                      type="text" 
                      className="flex-1 outline-none text-xs" 
                      value={inputSubject}
                      onChange={(e) => setInputSubject(e.target.value)}
                      placeholder="Subject"
                    />
                  </div>
                </div>
              )}
              
              <div className="p-2">
                <textarea
                  ref={inputRef}
                  className="w-full outline-none resize-none min-h-[50px] text-xs"
                  placeholder="Compose email"
                  value={inputValue}
                  onChange={handleTextareaChange}
                  rows={3}
                />
              </div>
              
              <div className="bg-gray-100 px-3 py-1.5 flex items-center">
                <button 
                  onClick={handleSendMessage}
                  disabled={inputValue.trim() === '' || (!replyMode && (inputTo.trim() === '' || inputSubject.trim() === ''))}
                  className="px-3 py-0.5 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
                >
                  Send
                </button>
                <button className="ml-2 p-1.5 text-gray-500 hover:text-gray-700">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M15 6l-4 4h8V2h-4v4zM2 12.5C2 9.46 4.46 7 7.5 7H18v2.5H7.5C5.84 9.5 4.5 10.84 4.5 12.5c0 1.66 1.34 3 3 3H18V18H7.5C4.46 18 2 15.54 2 12.5z" />
                  </svg>
                </button>
                <button className="p-1.5 text-gray-500 hover:text-gray-700">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GmailInterface;